import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PlatformService } from './platform.service';
import { StorageService } from './storage/storage.service';

export interface CrossTabMessage {
  type: 'AUTH_STATE_CHANGE' | 'USER_DATA_CHANGE' | 'SIGN_OUT';
  data?: Record<string, unknown>;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class CrossTabCommunicationService {
  private readonly STORAGE_KEY = 'crossTabAuthState';
  private messageSubject = new BehaviorSubject<CrossTabMessage | null>(null);

  constructor(
    private platformService: PlatformService,
    private storageService: StorageService,
  ) {
    if (this.platformService.isBrowser()) {
      window.addEventListener('storage', (event) => {
        if (event.key === this.STORAGE_KEY && event.newValue) {
          try {
            const message: CrossTabMessage = JSON.parse(event.newValue);
            // Storage event only fires in OTHER tabs (not the tab that sets the value)
            // This prevents infinite loops
            this.messageSubject.next(message);
          } catch (error) {
            console.error('Error parsing cross-tab message:', error);
          }
        }
      });
    }
  }

  /**
   * Broadcast authentication state change to all tabs
   */
  public broadcastAuthStateChange(isAuthenticated: boolean, userData?: Record<string, unknown>): void {
    const message: CrossTabMessage = {
      type: 'AUTH_STATE_CHANGE',
      data: { isAuthenticated, userData },
      timestamp: Date.now(),
    };

    this.broadcastMessage(message);
  }

  /**
   * Broadcast user data change to all tabs
   */
  public broadcastUserDataChange(userData: Record<string, unknown>): void {
    const message: CrossTabMessage = {
      type: 'USER_DATA_CHANGE',
      data: userData,
      timestamp: Date.now(),
    };

    this.broadcastMessage(message);
  }

  /**
   * Broadcast sign out to all tabs
   */
  public broadcastSignOut(): void {
    const message: CrossTabMessage = {
      type: 'SIGN_OUT',
      timestamp: Date.now(),
    };

    this.broadcastMessage(message);
  }

  /**
   * Listen for authentication state changes from other tabs
   */
  public onAuthStateChange(): Observable<CrossTabMessage> {
    return this.messageSubject
      .asObservable()
      .pipe(filter((message): message is CrossTabMessage => message?.type === 'AUTH_STATE_CHANGE'));
  }

  /**
   * Listen for user data changes from other tabs
   */
  public onUserDataChange(): Observable<CrossTabMessage> {
    return this.messageSubject
      .asObservable()
      .pipe(filter((message): message is CrossTabMessage => message?.type === 'USER_DATA_CHANGE'));
  }

  /**
   * Listen for sign out events from other tabs
   */
  public onSignOut(): Observable<CrossTabMessage> {
    return this.messageSubject
      .asObservable()
      .pipe(filter((message): message is CrossTabMessage => message?.type === 'SIGN_OUT'));
  }

  /**
   * Listen for any cross-tab messages
   */
  public onMessage(): Observable<CrossTabMessage> {
    return this.messageSubject.asObservable().pipe(filter((message) => message !== null));
  }

  private broadcastMessage(message: CrossTabMessage): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    try {
      // Set in StorageService - this will trigger storage event in OTHER tabs only
      // (storage events don't fire in the tab that sets the value)
      // We don't notify the current tab because it already initiated the action
      this.storageService.setItem(this.STORAGE_KEY, JSON.stringify(message));

      // Remove the item after a short delay to allow other tabs to process it
      // This prevents the message from persisting and being processed on page reload
      setTimeout(() => {
        try {
          this.storageService.removeItem(this.STORAGE_KEY);
        } catch (error) {
          // Ignore errors when cleaning up
        }
      }, 100);
    } catch (error) {
      console.error('Error broadcasting cross-tab message:', error);
    }
  }
}

